import React, { useRef, useState } from 'react';
import Icon from './Icon';
import Popover from './Popover';

/**
 * CkDropdown — custom select replacing native <select>. Drop-in for the
 * DS Select: same props (label, options, value, onChange, size);
 * onChange receives { target: { value } }. The menu renders through
 * ui/Popover (portaled), so it floats above panels, tables and any
 * transformed ancestor. Keyboard support (Enter/Space/Arrows/Escape).
 */
export interface DropdownOption {
  value: string;
  label: React.ReactNode;
}

export interface DropdownProps {
  label?: string;
  options: Array<string | DropdownOption>;
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
  placeholder?: string;
}

export default function Dropdown({
  label,
  options = [],
  value,
  onChange,
  size = 'md',
  style,
  placeholder = 'Select...',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const opts: DropdownOption[] = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = opts.find((o) => o.value === value);

  const pick = (v: string) => {
    onChange({ target: { value: v } });
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setOpen(true);
      setHi(Math.max(0, opts.findIndex((o) => o.value === value)));
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(opts.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hi >= 0) pick(opts[hi].value);
    }
  };

  const h = size === 'sm' ? 'var(--control-sm)' : 'var(--control-md)';

  return (
    <div style={{ position: 'relative', ...style }}>
      {label ? <div className="t-eyebrow" style={{ marginBottom: '7px' }}>{label}</div> : null}
      <button
        ref={buttonRef}
        type="button"
        className="pressable"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          setHi(Math.max(0, opts.findIndex((x) => x.value === value)));
        }}
        onKeyDown={onKey}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          width: '100%',
          minWidth: '120px',
          height: h,
          padding: '0 11px 0 12px',
          background: 'var(--surface-raised)',
          border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
          borderRadius: 'var(--radius-md)',
          color: selected ? 'var(--text-body)' : 'var(--text-faint)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--text-sm)',
          cursor: 'pointer',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected ? selected.label : placeholder}</span>
        <Icon
          name="chevron-down"
          size={12}
          style={{
            color: 'var(--text-faint)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-out)',
          }}
        />
      </button>

      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        matchWidth
        role="listbox"
        style={{ maxHeight: '264px' }}
      >
        {opts.map((o, i) => {
          const isSel = o.value === value;
          return (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={isSel}
              className="pressable"
              onClick={() => pick(o.value)}
              onMouseEnter={() => setHi(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                textAlign: 'left',
                background: hi === i ? 'var(--surface-active)' : 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: (isSel ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              {isSel ? <Icon name="check" size={12} style={{ color: 'var(--accent-text)' }} /> : null}
            </button>
          );
        })}
      </Popover>
    </div>
  );
}
